import { CalculatePriceResponse } from "core/calculatePrice/calculatePrice.api";
import { CalculatePriceThunk } from "core/calculatePrice/calculatePrice.thunk";
import { AdSpot, targetType } from "core/orderAd/orderAd.entity";
import { useEffect, useRef, useState } from "react";
import { WebLogger } from "utils/logger";
import { useRequest } from "./useRequest";

function hashData(data: {
  link: string;
  viewCount: number;
  clickCount: number;
  spot: AdSpot;
  is_instant?: boolean;
}) {
  return JSON.stringify(data);
}

interface CalculatePriceHookParams {
  link: string;
  viewCount: number;
  clickCount: number;
  spot: AdSpot;
  is_instant?: boolean;
  target_type?: targetType;
}

interface CalculatePriceHook {
  finalPrice: number;
  finalBasePrice: number;
  totalPrice: number;
  loading: boolean;
}

export const useCalculatedPrice = (
  data: CalculatePriceHookParams
): CalculatePriceHook => {
  const [finalPrice, setFinalPrice] = useState<number>(0);
  const [finalBasePrice, setFinalBasePrice] = useState<number>(0);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const { sendRequest: calculatePrice } = useRequest(CalculatePriceThunk);

  const cachedPrices = useRef<{ [key: string]: CalculatePriceResponse }>({});

  useEffect(() => {
    if (!loading && (data.viewCount || data.clickCount)) {
      if (cachedPrices.current[hashData(data)]) {
        const response = cachedPrices.current[hashData(data)];
        setFinalPrice(response.final_price);
        setFinalBasePrice(response.final_base_price);
        setTotalPrice(response.total_price);
        return;
      }
      setLoading(true);
      calculatePrice({
        link: data.link,
        spot: data.spot,
        view_count: data.viewCount,
        click_count: data.clickCount,
        is_instant: data.is_instant,
        target_type: data.target_type,
        onSuccess: (response) => {
          if (!cachedPrices.current[hashData(data)]) {
            cachedPrices.current[hashData(data)] = response;
          }
          setFinalPrice(response.final_price);
          setFinalBasePrice(response.final_base_price);
          setTotalPrice(response.total_price);
        },
        onError: (e) => {
          WebLogger.error("getCalculatedPrice", e);
        },
      }).finally(() => {
        setLoading(false);
      });
    }
  }, [
    loading,
    data.clickCount,
    data.spot,
    data.link,
    data.viewCount,
    data.is_instant,
    data.target_type,
  ]);

  return {
    loading,
    finalPrice,
    finalBasePrice,
    totalPrice,
  };
};
